import { useTenantApi } from '@/account/hook/useTenantApi';
import { cartQueryKey, listOrdersQueryKey, registrationQueryKey } from '@/api/registration';
import { createNabTransactPayment, type NabTransactOptions } from '@/api/registration/payment';
import CardNabForm from '@/registration/component/CardNabForm';
import { useRegisterRoute } from '@/registration/hook/useRegisterRoute';
import { useRegisterSteps } from '@/registration/hook/useRegisterSteps';
import { useRegistrationQuery } from '@/registration/hook/useRegistrationQuery';
import { useUserSession } from '@/registration/hook/useUserSession';
import { useQueryClient } from '@tanstack/react-query';
import { type FC } from 'react';

export interface PayByNabTransactProps {
  setErrors: React.Dispatch<React.SetStateAction<string[]>>
  options: NabTransactOptions
  isValid: boolean
}

interface FormData {
  cardNumber: string
  expiry: string
  cvv: string
}

const encodeData = (data: string): string => {
  return btoa(data);
};

const PayByNabTransact: FC<PayByNabTransactProps> = ({ setErrors, options }) => {
  const { toPaymentProcessingStep } = useRegisterSteps();
  const { createTenantApiRequest } = useTenantApi();
  const { eventId } = useRegisterRoute();
  const queryClient = useQueryClient();
  const { userSession } = useUserSession();
  const registrationQuery = useRegistrationQuery(eventId);

  const handleSubmit = async (values: FormData) => {
    try {
      const cardNumber = values.cardNumber.split(' ').join('');
      const [expiryMonth, expiryYear] = values.expiry.split('/');
      const response = await createNabTransactPayment(createTenantApiRequest)({
        orderId: options.order.id,
        ccNumber: encodeData(cardNumber),
        ccMonth: encodeData(expiryMonth),
        ccYear: encodeData(expiryYear),
        ccCvv: encodeData(values.cvv.toString())
      });

      if (!response.errors) {
        await queryClient.invalidateQueries({ queryKey: [registrationQuery] });
        await queryClient.invalidateQueries({ queryKey: [listOrdersQueryKey, { eventId, userSession }] });
        await queryClient.invalidateQueries({ queryKey: [registrationQueryKey, { eventId }, { token: userSession?.authToken }] });
        await queryClient.invalidateQueries({ queryKey: [cartQueryKey, { eventId, userSession }] });
        const additionalParams = { orderHash: options.order.secureHash };
        toPaymentProcessingStep(additionalParams);
      } else {
        setErrors([response.errors?.[0] ?? 'Payment failed']);
      }
    } catch (error) {
      setErrors(['An error occurred while processing the payment']);
    }
  };

  return (
    <CardNabForm handleSubmit={handleSubmit} />
  );
};

export default PayByNabTransact;
